import { useEffect, useMemo, useState } from "react";
import api, { downloadFile } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Checkbox } from "./ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { ChevronRight, ListFilter, AlertTriangle, FileDown, FileSpreadsheet } from "lucide-react";
import { toast } from "sonner";

const chipClass = (v) => (v >= 85 ? "bg-emerald-100 text-emerald-800" : v >= 70 ? "bg-amber-100 text-amber-800" : "bg-rose-100 text-rose-700");

function Cell({ value, onClick, testid }) {
  if (value == null) return <span className="inline-block min-w-[52px] px-2 py-1 rounded-md text-xs text-stone-300 bg-stone-50">—</span>;
  return (
    <button data-testid={testid} onClick={onClick} disabled={!onClick}
      className={`inline-block min-w-[52px] px-2 py-1 rounded-md font-mono text-xs font-semibold transition-transform ${chipClass(value)} ${onClick ? "hover:scale-110 hover:shadow-sm cursor-pointer" : ""}`}>
      {value}%
    </button>
  );
}

export const DeptMatrix = ({ filters }) => {
  const { user } = useAuth();
  const hotelScoped = ["hotel_gm", "dept_head"].includes(user.role);
  const [region, setRegion] = useState(null);
  const [data, setData] = useState(null);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);

  const params = useMemo(() => {
    const p = {};
    if (filters.template_id !== "all") p.template_id = filters.template_id;
    if (filters.audit_type !== "all") p.audit_type = filters.audit_type;
    if (filters.hotel_id !== "all") p.hotel_id = filters.hotel_id;
    return p;
  }, [filters]);

  const effRegion = region || (filters.region !== "all" ? filters.region : null);
  const hotelView = hotelScoped || !!effRegion || !!params.hotel_id;

  useEffect(() => {
    api.get("/dashboard/dept-matrix", { params: { ...params, group_by: hotelView ? "hotel" : "region", ...(effRegion ? { region: effRegion } : {}) } })
      .then((r) => { setData(r.data); setSelected(null); })
      .catch(() => {});
  }, [params, effRegion, hotelView]);

  const defaultSelection = useMemo(() => {
    if (!data) return [];
    return data.hotels_in_scope.slice(0, 8).map((h) => h.id);
  }, [data]);

  const activeSelection = selected || defaultSelection;
  const visibleColumns = useMemo(() => {
    if (!data) return [];
    if (data.group_by !== "hotel") return data.columns;
    const set = new Set(activeSelection);
    return data.columns.filter((c) => set.has(c.key));
  }, [data, activeSelection]);

  if (!data || data.rows.length === 0) return null;
  const totalHotels = data.hotels_in_scope.length;

  const openDetail = async (dept, col) => {
    setDetail({ department: dept, label: col ? col.label : effRegion || "All hotels", loading: true });
    try {
      const p = { department: dept, ...params };
      if (data.group_by === "hotel" && col) p.hotel_id = col.key;
      else if (col) p.region = col.key;
      else if (effRegion) p.region = effRegion;
      const r = await api.get("/dashboard/dept-matrix/detail", { params: p });
      setDetail((d) => ({ ...d, loading: false, payload: r.data }));
    } catch {
      setDetail(null);
    }
  };

  const toggleHotel = (id) => {
    const next = new Set(activeSelection);
    next.has(id) ? next.delete(id) : next.add(id);
    setSelected([...next]);
  };

  const exportMatrix = async (fmt) => {
    const q = new URLSearchParams({ ...params, group_by: data.group_by });
    if (effRegion) q.set("region", effRegion);
    if (data.group_by === "hotel" && visibleColumns.length < data.columns.length) q.set("hotel_ids", activeSelection.join(","));
    try {
      await downloadFile(`/dashboard/dept-matrix/export/${fmt}?${q.toString()}`, `departmental-scorecard.${fmt === "pdf" ? "pdf" : "xlsx"}`);
      toast.success("Scorecard downloaded");
    } catch {
      toast.error("Could not generate export");
    }
  };

  return (
    <div className="bg-white border border-[#E7E5DF] rounded-xl p-6" data-testid="dept-matrix">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-1">
        <div>
          <h3 className="font-semibold text-stone-800">Departmental Dashboard</h3>
          <div className="flex items-center gap-1 text-[11px] text-stone-400 mt-0.5" data-testid="matrix-breadcrumb">
            {!hotelScoped && !params.hotel_id ? (
              <>
                <button data-testid="matrix-all-regions" className={`${effRegion ? "text-[#14532D] font-semibold hover:underline" : ""}`} onClick={() => setRegion(null)} disabled={!region}>
                  All Clusters
                </button>
                {effRegion && (<><ChevronRight className="w-3 h-3" /><span className="font-semibold text-stone-600">{effRegion}</span></>)}
                {!effRegion && <span>— click a cluster to drill into its hotels, click a score for the findings behind it</span>}
              </>
            ) : (
              <span>Department scores{hotelScoped ? " for your hotel" : ""} — click a score for the findings behind it</span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {data.group_by === "hotel" && totalHotels > 1 && (
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" size="sm" data-testid="matrix-hotel-picker">
                  <ListFilter className="w-3.5 h-3.5 mr-1.5" />Compare hotels ({visibleColumns.length}/{totalHotels})
                </Button>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-80 max-h-96 overflow-y-auto p-3">
                <p className="text-xs font-semibold text-stone-500 uppercase tracking-wider mb-2">Select hotels to compare</p>
                {data.hotels_in_scope.map((h) => (
                  <label key={h.id} className="flex items-center gap-2.5 py-1.5 cursor-pointer" data-testid={`matrix-pick-${h.id}`}>
                    <Checkbox checked={activeSelection.includes(h.id)} onCheckedChange={() => toggleHotel(h.id)} />
                    <span className="text-sm text-stone-700 flex-1 truncate">{h.name}</span>
                    <span className="font-mono text-xs text-stone-400">{h.avg != null ? `${h.avg}%` : "—"}</span>
                  </label>
                ))}
              </PopoverContent>
            </Popover>
          )}
          <Button variant="outline" size="sm" data-testid="matrix-export-pdf" onClick={() => exportMatrix("pdf")}>
            <FileDown className="w-3.5 h-3.5 mr-1.5" />PDF
          </Button>
          <Button variant="outline" size="sm" data-testid="matrix-export-excel" onClick={() => exportMatrix("excel")}>
            <FileSpreadsheet className="w-3.5 h-3.5 mr-1.5" />Excel
          </Button>
        </div>
      </div>

      <div className="overflow-x-auto mt-4">
        <table className="w-full">
          <thead>
            <tr className="text-left text-[11px] uppercase tracking-wider text-stone-500 border-b border-[#E7E5DF]">
              <th className="pb-2 pr-3">Department</th>
              {visibleColumns.map((c) => (
                <th key={c.key} className="pb-2 px-2 text-center max-w-[130px]">
                  {data.group_by === "region" ? (
                    <button data-testid={`matrix-region-${c.key}`} className="block w-full truncate text-[#14532D] hover:underline font-bold" title={`Drill into ${c.label}`} onClick={() => setRegion(c.key)}>{c.label}</button>
                  ) : (
                    <span className="block truncate" title={c.label}>{c.label}</span>
                  )}
                </th>
              ))}
              <th className="pb-2 pl-2 text-center whitespace-nowrap">{data.group_by === "hotel" ? `Avg (all ${totalHotels})` : "Portfolio Avg"}</th>
            </tr>
          </thead>
          <tbody>
            {data.rows.map((r) => (
              <tr key={r.department} className="border-b border-stone-100 last:border-0" data-testid={`dept-row-${r.department}`}>
                <td className="py-2.5 pr-3 text-sm font-medium text-stone-800 whitespace-nowrap">{r.department}</td>
                {visibleColumns.map((c) => (
                  <td key={c.key} className="py-1.5 px-2 text-center">
                    <Cell value={r.cells[c.key]} testid={`matrix-cell-${r.department}-${c.key}`}
                      onClick={r.cells[c.key] != null ? () => (data.group_by === "region" ? setRegion(c.key) : openDetail(r.department, c)) : null} />
                  </td>
                ))}
                <td className="py-1.5 pl-2 text-center">
                  <button data-testid={`matrix-benchmark-${r.department}`} className="font-mono text-xs font-bold text-stone-700 hover:text-[#14532D] hover:underline" onClick={() => openDetail(r.department, null)}>{r.benchmark}%</button>
                </td>
              </tr>
            ))}
            <tr className="border-t border-[#E7E5DF]">
              <td className="py-2.5 pr-3 text-xs font-bold uppercase tracking-wider text-stone-500">{data.group_by === "hotel" ? "Hotel Avg" : "Cluster Avg"}</td>
              {visibleColumns.map((c) => (
                <td key={c.key} className="py-2.5 px-2 text-center font-mono text-xs font-bold text-stone-700">{data.column_avgs[c.key] != null ? `${data.column_avgs[c.key]}%` : "—"}</td>
              ))}
              <td />
            </tr>
          </tbody>
        </table>
      </div>

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="matrix-detail-dialog">
          <DialogHeader>
            <DialogTitle className="tracking-tight">{detail?.department} — {detail?.label}</DialogTitle>
          </DialogHeader>
          {detail?.loading ? (
            <div className="flex justify-center py-12"><div className="w-6 h-6 border-2 border-[#14532D] border-t-transparent rounded-full animate-spin" /></div>
          ) : detail?.payload && (
            <div className="space-y-5">
              <div>
                <h4 className="text-xs font-semibold uppercase tracking-wider text-stone-500 mb-2">Contributing Audits</h4>
                {detail.payload.contributing.map((c, i) => (
                  <div key={`${c.template}-${c.hotel}-${c.date}-${i}`} className="flex items-center justify-between py-1.5 border-b border-stone-100 last:border-0 text-sm">
                    <span className="text-stone-700 truncate pr-3">{c.template} · <span className="text-stone-500">{c.hotel}</span></span>
                    <span className="flex items-center gap-3 shrink-0">
                      <span className="font-mono text-xs text-stone-400">{c.date}</span>
                      <span className={`px-2 py-0.5 rounded-md font-mono text-xs font-semibold ${chipClass(c.score)}`}>{c.score}%</span>
                    </span>
                  </div>
                ))}
              </div>
              <div>
                <h4 className="text-xs font-semibold uppercase tracking-wider text-stone-500 mb-2">Failed Points ({detail.payload.failed_questions.length})</h4>
                {detail.payload.failed_questions.length === 0 ? (
                  <p className="text-sm text-stone-400 py-4 text-center">No failed questions — score reflects partial credits.</p>
                ) : detail.payload.failed_questions.map((f, i) => (
                  <div key={`${f.question}-${i}`} className="py-2 border-b border-stone-100 last:border-0">
                    <div className="flex items-start justify-between gap-3">
                      <p className="text-sm text-stone-800">{f.question}</p>
                      <span className="shrink-0 font-mono text-xs font-bold text-rose-600">-{f.points_lost} pts</span>
                    </div>
                    <div className="flex flex-wrap items-center gap-2 mt-1 text-[11px] text-stone-400">
                      {f.critical && <span className="inline-flex items-center gap-1 font-bold text-rose-600 uppercase"><AlertTriangle className="w-3 h-3" />Critical</span>}
                      <span>{f.hotel}</span><span>·</span><span>{f.section}</span><span>·</span><span>{f.date}</span>
                      {f.comment && <span className="w-full text-stone-500 italic">“{f.comment}”</span>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
